import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createStorageAttachment,
  CreateStorageAttachmentParams,
  deleteStorageAttachment,
  StorageAttachmentRecordType,
} from "./api"

const RECORD_QUERY_KEYS: Record<StorageAttachmentRecordType, string[]> = {
  Task: ["task", "tasks"],
  TaskTemplate: ["task-templates", "company-task-templates", "project-template", "project-milestone"],
  Project: ["project", "projects"],
  ProjectTemplate: ["project-template", "project-templates"],
  Meeting: ["meeting", "meetings"],
}

function invalidateRecordQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  recordType: StorageAttachmentRecordType
) {
  RECORD_QUERY_KEYS[recordType].forEach((key) => {
    queryClient.invalidateQueries({ queryKey: [key] })
  })
}

export function useCreateStorageAttachment() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateStorageAttachmentParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createStorageAttachment(context, params)
    },
    meta: { errorToast: { fallback: "Failed to attach the link" } },
    onSuccess: (_data, variables) => {
      invalidateRecordQueries(queryClient, variables.recordType)
    },
  })
}

export function useDeleteStorageAttachment() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: { id: string; recordType: StorageAttachmentRecordType }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteStorageAttachment(context, id)
    },
    meta: { errorToast: { fallback: "Failed to remove the link" } },
    onSuccess: (_data, variables) => {
      invalidateRecordQueries(queryClient, variables.recordType)
    },
  })
}
